// react
import React, { useMemo, useRef } from 'react';

// raptor
import theme from '@raptormaps/theme';
import { SolarFarm as SolarFarmModel } from '@raptormaps/raptor-models';

// third party
import Map, { Layer, Source } from 'react-map-gl/mapbox';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { FeatureCollection, Polygon } from 'geojson';
import { feature } from '@turf/helpers';

// local
import { DrawControl } from './DrawControl';
import { SolarFarm } from '@raptormaps/raptor-internal-client-ts';
import { raptorKeys } from '@raptormaps/raptor-lib';

interface FlightBoundaryMapProps {
  solarFarm: SolarFarm | SolarFarmModel;
  boundary?: Polygon;
  zoneBoundaries?: Polygon[];
  onBoundaryUpdate?: (boundary: Polygon | null) => void;
}

export const FlightBoundaryMap = ({
  boundary,
  zoneBoundaries = [],
  onBoundaryUpdate,
  solarFarm,
}: FlightBoundaryMapProps) => {
  const drawRef = useRef<MapboxDraw | null>(null);

  const zonesFeatureCollection: FeatureCollection = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: zoneBoundaries.map(geom => feature(geom)),
    }),
    [zoneBoundaries],
  );

  return (
    <Map
      initialViewState={{
        // @ts-ignore
        longitude: solarFarm.longitude ?? solarFarm.location.lng,
        // @ts-ignore
        latitude: solarFarm.latitude ?? solarFarm.location.lat,
        zoom: 17,
      }}
      mapboxAccessToken={raptorKeys.mapboxToken}
      mapStyle="mapbox://styles/mapbox/satellite-v9"
    >
      {onBoundaryUpdate ? (
        <DrawControl
          ref={drawRef}
          features={boundary ? [feature(boundary)] : []}
          displayControlsDefault={false}
          controls={{ polygon: true, trash: true }}
          onCreate={event => {
            if (drawRef.current) {
              const featureCollection = drawRef.current.getAll();
              if (featureCollection.features.length < 2) {
                // There is no existing box
                const box = event.features[0].geometry;
                if (box.type === 'Polygon') {
                  onBoundaryUpdate(box);
                }
              } else if (featureCollection.features[0].id) {
                // There is an existing box, remove and create the new box
                drawRef.current.delete(
                  featureCollection.features[0].id.toString(),
                );
                const box = event.features[0].geometry;

                if (box.type === 'Polygon') {
                  onBoundaryUpdate(box);
                }
              }
            }
          }}
          onUpdate={event => {
            const box = event.features[0].geometry;

            if (box.type === 'Polygon') {
              onBoundaryUpdate(box);
            }
          }}
          onDelete={() => onBoundaryUpdate(null)}
        />
      ) : null}
      <Source id="inspectionAreas" type="geojson" data={zonesFeatureCollection}>
        <Layer
          id="inspectionZoneLine"
          type="line"
          source="inspectionAreas"
          paint={{
            'line-color': theme.colors.primary_400,
            'line-width': 2,
          }}
        />
      </Source>
    </Map>
  );
};
